/* eslint-disable max-params */
/* eslint-disable no-unused-vars */
import { DetailsType } from 'types/application';
import aecPayload from './aec';
import eventPayload from './event';
import filmsPayload from './films';
import licencePayload from './licence';
import pubPayload from './pub';
import pubTitlePayload from './pubTitle';
import videogamesPayload from './videogames';
import interactiveSoftwarePayload from './interactiveSoftware';
import { CASE_TYPE_ID } from 'common/constants';
import roePayload from './roe';
import acaPayload from './aca';
import aaPayload from './aa';

type Props = {
    myInfoContent: any;
    application?: string;
    uen?: string;
    id?: string;
    userRole?: string;
    applicationType?: string;
};

export const formatAutofill = (props: Props) => {
    // ROE routing: either direct ROE case type or generic LICApplication with applicationType label
    if (
        props.application === 'IMDA-IRIS-Work-LICApplication-ROE' ||
        (props.application === 'IMDA-IRIS-Work-LICApplication' && props.applicationType === 'Radio Operator Exam')
    ) {
        return roePayload.autoFillData(props);
    }

    if (
        props.application === 'IMDA-IRIS-Work-LICApplication-ACA' ||
        (props.application === 'IMDA-IRIS-Work-LICApplication' && props.applicationType === 'Accreditation of Certification Authority')
    ) {
        return acaPayload.autoFillData(props);
    }

    // AA routing: either direct AA case type or generic LICApplication with applicationType label
    if (
        props.application === 'IMDA-IRIS-Work-LICApplication-AA' ||
        (props.application === 'IMDA-IRIS-Work-LICApplication' && props.applicationType === 'Registration of Accounting Authority')
    ) {
        return aaPayload.autoFillData(props);
    }

    switch (props.application) {
        case 'IMDA-IRIS-Work-Arts': {
            return aecPayload.autoFillData(props);
        }
        case 'IMDA-IRIS-Work-Publication': {
            return pubPayload.autoFillData(props);
        }
        case CASE_TYPE_ID.FILMS: {
            return filmsPayload.autoFillData(props);
        }
        case CASE_TYPE_ID.VIDEO_GAMES: {
            return videogamesPayload.autoFillData(props);
        }
        case CASE_TYPE_ID.INTERACTIVE_SOFTWARE: {
            return interactiveSoftwarePayload.autoFillData(props);
        }
        default: {
            return null;
        }
    }
};

// Helper: identify ROE application whether direct or via generic licence + applicationType label
export const isROEApplication = (application: string, applicationType?: string) =>
    application === 'IMDA-IRIS-Work-LICApplication-ROE' ||
    (application === 'IMDA-IRIS-Work-LICApplication' && applicationType === 'Radio Operator Exam');

export const isACAApplication = (application: string, applicationType?: string) =>
    application === 'IMDA-IRIS-Work-LICApplication-ACA' ||
    (application === 'IMDA-IRIS-Work-LICApplication' && applicationType === 'Accreditation of Certification Authority');

export const isAAApplication = (application: string, applicationType?: string) =>
    application === 'IMDA-IRIS-Work-LICApplication-AA' ||
    (application === 'IMDA-IRIS-Work-LICApplication' && applicationType === 'Registration of Accounting Authority');

export const formatRetrieve = async (
    content: any,
    application: string,
    caseTypeID: string,
    action?: string,
    applicationForm?: string,
    applicationType?: string,
    userRole?: string,
    selectedTitleSearch?: any,
    dropdownOpts?: any,
    newApplication?: boolean,
    isActiveDistributionLicenceCompany?: any,
    CAProfileDetails?: any
) => {
    // ROE routing: direct case type or legacy licence + applicationType combo
    if (
        application === 'IMDA-IRIS-Work-LICApplication-ROE' ||
        application === 'IMDA-IRIS-Work-LICApplication-ROE-VIEW' ||
        (application === 'IMDA-IRIS-Work-LICApplication' && (applicationType === 'Radio Operator Exam' || content?.licenceName === 'Radio Operator Exam'))
    ) {
        // Pass nationality raw cache if present (stored separately in state) AND dropdown options for fallback.
        const nationalityRaw = dropdownOpts?.nationalityData || undefined;
        return await roePayload.formatRetrieve(content, action, nationalityRaw, dropdownOpts, application, caseTypeID);
    }

    if (
        application === 'IMDA-IRIS-Work-LICApplication-ACA' ||
        application === 'IMDA-IRIS-Work-LICApplication-ACA-AMEND' ||
        application === 'IMDA-IRIS-Work-LICApplication-ACA-RENEW' ||
        application === 'IMDA-IRIS-Work-LICApplication-ACA-RENEW-NOPAY' ||
        application === 'IMDA-IRIS-Work-LICApplication-ACA-VIEW' ||
        application === 'IMDA-IRIS-Work-LICApplication-ACA-RENEW-VIEW' ||
        (application === 'IMDA-IRIS-Work-LICApplication' &&
            (applicationType === 'Accreditation of Certification Authority' ||
                content?.licenceName === 'Accreditation of Certification Authority'))
    ) {
        // ACA no longer uses nationality - ID validation based on selected ID type
        return await acaPayload.formatRetrieve(content, action, dropdownOpts, application, caseTypeID);
    }

    // AA routing: direct case type or legacy licence + applicationType combo
    if (
        application === 'IMDA-IRIS-Work-LICApplication-AA' ||
        application === 'IMDA-IRIS-Work-LICApplication-AA-VIEW' ||
        (application === 'IMDA-IRIS-Work-LICApplication' &&
            (applicationType === 'Registration of Accounting Authority' || content?.licenceName === 'Registration of Accounting Authority'))
    ) {
        // Pass nationality raw cache if present (stored separately in state) AND dropdown options for fallback.
        const nationalityRaw = dropdownOpts?.nationalityData || undefined;
        return await aaPayload.formatRetrieve(content, action, nationalityRaw, dropdownOpts, application, caseTypeID);
    }

    switch (application) {
        case 'IMDA-IRIS-Work-Arts': {
            const forms = await aecPayload.formatRetrieve(content, action);
            return forms;
        }
        case 'IMDA-IRIS-Work-Events': {
            const forms = await eventPayload.formatRetrieve(content, caseTypeID, action);
            return forms;
        }
        case 'IMDA-IRIS-Work-Publication': {
            const forms = await pubPayload.formatRetrieve(content, action);
            return forms;
        }
        case 'IMDA-IRIS-Work-PublicationTitles': {
            const forms = await pubTitlePayload.formatRetrieve(content, action);
            return forms;
        }
        case 'IMDA-IRIS-Work-Licence':
        case 'IMDA-IRIS-Work-Licence-MNPP':
        case 'IMDA-IRIS-Work-Licence-BCG':
        case 'IMDA-IRIS-Work-Licence-TELE': {
            const forms = await licencePayload.formatRetrieve(content, caseTypeID, action, application);
            return forms;
        }
        case 'IMDA-IRIS-Work-LICApplication':
        case 'IMDA-IRIS-Work-LICApplication-MNPP':
        case 'IMDA-IRIS-Work-LICApplication-BCG':
        case 'IMDA-IRIS-Work-LICApplication-TELE': {
            const forms = await licencePayload.formatRetrieve(content, caseTypeID, action, application);
            return forms;
        }
        case 'IMDA-IRIS-Work-LICApplication-ROE':
        case 'IMDA-IRIS-Work-LICApplication-ROE-VIEW': {
            const nationalityRaw = dropdownOpts?.nationalityData || undefined;
            const forms = await roePayload.formatRetrieve(content, action, nationalityRaw, dropdownOpts, application);
            return forms;
        }
        case 'IMDA-IRIS-Work-LICApplication-ACA':
        case 'IMDA-IRIS-Work-LICApplication-ACA-AMEND':
        case 'IMDA-IRIS-Work-LICApplication-ACA-RENEW':
        case 'IMDA-IRIS-Work-LICApplication-ACA-RENEW-NOPAY':
        case 'IMDA-IRIS-Work-LICApplication-ACA-VIEW':
        case 'IMDA-IRIS-Work-LICApplication-ACA-AMEND-VIEW':
        case 'IMDA-IRIS-Work-LICApplication-ACA-RENEW-VIEW':
        case 'IMDA-IRIS-Work-LICApplication-ACA-CANCEL': {
            // ACA no longer uses nationality
            const forms = await acaPayload.formatRetrieve(content, action, dropdownOpts, application);
            return forms;
        }
        case 'IMDA-IRIS-Work-LICApplication-AA':
        case 'IMDA-IRIS-Work-LICApplication-AA-VIEW':
        case 'IMDA-IRIS-Work-LICApplication-AA-CANCEL': {
            const nationalityRaw = dropdownOpts?.nationalityData || undefined;
            const forms = await aaPayload.formatRetrieve(content, action, nationalityRaw, dropdownOpts, application);
            return forms;
        }
        case 'IMDA-FVGCS-Work-FVG-FilmTitles':
        case 'IMDA-FVGCS-Work-FVG-RCAFlow':
        case CASE_TYPE_ID.FILMS: {
            const forms = await filmsPayload.formatRetrieve(
                content,
                caseTypeID,
                action,
                applicationForm,
                applicationType,
                userRole,
                dropdownOpts,
                selectedTitleSearch,
                isActiveDistributionLicenceCompany,
                newApplication,
                CAProfileDetails
            );
            return forms;
        }
        case CASE_TYPE_ID.VIDEO_GAMES: {
            const forms = await videogamesPayload.formatRetrieve(
                content,
                caseTypeID,
                action,
                applicationForm,
                userRole,
                dropdownOpts,
                newApplication
            );
            return forms;
        }
        case CASE_TYPE_ID.INTERACTIVE_SOFTWARE: {
            const forms = await interactiveSoftwarePayload.formatRetrieve(
                content,
                caseTypeID,
                action,
                applicationForm,
                userRole,
                dropdownOpts,
                newApplication
            );
            return forms;
        }
        default: {
            return [];
        }
    }
};

type SaveProps = {
    details: DetailsType;
    application: string;
    caseTypeID: string;
    action?: string;
    pageInstructions?: any[];
    userRole?: string;
    applicationForm?: string;
    applicationType?: string;
    activeStep?: number;
    content?: any;
    selectedTitleSearch?: any;
    amendType?: 'Material' | 'Non-Material' | 'Both' | null;
    activeAccordion?: number | null;
    CAProfileUUID?: string;
    existingTitle?: string;
};

export const formatSave = async ({
    details,
    application,
    caseTypeID,
    action,
    pageInstructions,
    userRole,
    applicationForm,
    applicationType,
    activeStep,
    content,
    selectedTitleSearch,
    amendType,
    CAProfileUUID,
    existingTitle
}: SaveProps) => {
    switch (application) {
        case 'IMDA-IRIS-Work-LICApplication-ROE':
        case 'IMDA-IRIS-Work-LICApplication-ROE-VIEW': {
            const payload = await roePayload.formatSave(details, caseTypeID, pageInstructions, userRole, action);
            return payload;
        }
        case 'IMDA-IRIS-Work-LICApplication-ACA':
        case 'IMDA-IRIS-Work-LICApplication-ACA-AMEND':
        case 'IMDA-IRIS-Work-LICApplication-ACA-RENEW':
        case 'IMDA-IRIS-Work-LICApplication-ACA-RENEW-NOPAY':
        case 'IMDA-IRIS-Work-LICApplication-ACA-VIEW':
        case 'IMDA-IRIS-Work-LICApplication-ACA-AMEND-VIEW':
        case 'IMDA-IRIS-Work-LICApplication-ACA-CANCEL': {
            const payload = await acaPayload.formatSave(details, caseTypeID, pageInstructions, userRole, action, amendType);
            return payload;
        }
        case 'IMDA-IRIS-Work-LICApplication-AA':
        case 'IMDA-IRIS-Work-LICApplication-AA-VIEW':
        case 'IMDA-IRIS-Work-LICApplication-AA-CANCEL': {
            const payload = await aaPayload.formatSave(details, caseTypeID, pageInstructions, userRole, action);
            return payload;
        }
        case 'IMDA-IRIS-Work-LICApplication': {
            if (applicationType === 'Radio Operator Exam') {
                const payload = await roePayload.formatSave(details, caseTypeID, pageInstructions, userRole, action);
                return payload;
            }

            if (applicationType === 'Accreditation of Certification Authority') {
                const payload = await acaPayload.formatSave(details, caseTypeID, pageInstructions, userRole, action, amendType);
                return payload;
            }

            if (applicationType === 'Registration of Accounting Authority') {
                const payload = await aaPayload.formatSave(details, caseTypeID, pageInstructions, userRole, action);
                return payload;
            }
            // fall through to default empty until generic licence save implemented (or add licencePayload.formatSave if exists)
            break;
        }
        case 'IMDA-IRIS-Work-Arts': {
            const payload = await aecPayload.formatSave(details, caseTypeID, pageInstructions, userRole!, action);
            return payload;
        }
        case 'IMDA-IRIS-Work-Events': {
            const payload = await eventPayload.formatSave(details, caseTypeID, action);
            return payload;
        }
        case 'IMDA-IRIS-Work-Publication': {
            const payload = await pubPayload.formatSave(details, caseTypeID, pageInstructions, action);
            return payload;
        }
        case 'IMDA-IRIS-Work-PublicationTitles': {
            const payload = await pubTitlePayload.formatSave(details, action);
            return payload;
        }
        case 'IMDA-FVGCS-Work-FVG-FilmTitles':
        case 'IMDA-FVGCS-Work-FVG-RCAFlow':
        case CASE_TYPE_ID.FILMS: {
            const payload = await filmsPayload.formatSave(
                details,
                caseTypeID,
                applicationForm,
                applicationType,
                pageInstructions,
                userRole,
                action,
                selectedTitleSearch,
                content,
                CAProfileUUID,
                existingTitle
            );
            return payload;
        }
        case CASE_TYPE_ID.VIDEO_GAMES: {
            const payload = await videogamesPayload.formatSave(
                details,
                caseTypeID,
                pageInstructions,
                userRole,
                action,
                activeStep,
                content
            );
            return payload;
        }
        case CASE_TYPE_ID.INTERACTIVE_SOFTWARE: {
            const payload = await interactiveSoftwarePayload.formatSave(details, caseTypeID, pageInstructions, userRole, action);
            return payload;
        }
        default: {
            return [];
        }
    }
};

export const emptySet = (application: string) => {
    switch (application) {
        case 'IMDA-IRIS-Work-Arts': {
            return aecPayload.emptySet;
        }
        case 'IMDA-IRIS-Work-Publication': {
            return pubPayload.emptySet;
        }
        case 'IMDA-FVGCS-Work-FVG-FilmTitles':
        case CASE_TYPE_ID.FILMS: {
            return filmsPayload.emptySet;
        }
        default:
            return {};
    }
};
